/**
 * Widget "Scenarios bancaires" - Calcul automatique d'endettement futur
 * pour 3 budgets references (300k, 350k, 400k) sur 20 et 25 ans.
 *
 * Recupere les taux du jour via /api/market/interest-rates (Meilleurtaux scraping).
 * Pondere les loyers prevus a 80% (regle bancaire).
 * Plafond endettement HCSF : 35%.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mensualite d'un emprunt amortissable
const computeMonthly = (principal, annualRatePct, years) => {
  if (!principal || principal <= 0 || !years) return 0;
  const r = (annualRatePct / 100) / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return principal * r / (1 - Math.pow(1 + r, -n));
};

// Scenarios fixes (references conseillees)
const FIXED_SCENARIOS = [
  { budget: 300000, rent: 1900 },
  { budget: 350000, rent: 2500 },
  { budget: 400000, rent: 3200 },
];

const PONDERATION_LOYERS = 0.80; // 80% norme bancaire
const PLAFOND_ENDETTEMENT = 35;  // 35% HCSF

export const BankScenariosWidget = ({ form }) => {
  const [rates, setRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(true);

  useEffect(() => {
    axios.get(`${API}/market/interest-rates`)
      .then(r => setRates(r.data))
      .catch(() => setRates({ rates: { '20': 3.85, '25': 4.00 }, source: 'fallback' }))
      .finally(() => setLoadingRates(false));
  }, []);

  // Revenus actuels (avec ponderation 80% des loyers existants)
  // Co-emprunteur TOUJOURS inclus si renseigne (symetrique scoring iter 112)
  const rentalsListTotal = (form.rentals_list || []).reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const leadIncome = (parseFloat(form.monthly_net_income) || 0) + rentalsListTotal * PONDERATION_LOYERS;
  const coRentalsTotal = (form.co_borrower_rentals_list || []).reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const coIncome = (parseFloat(form.co_borrower_income) || 0) + coRentalsTotal * PONDERATION_LOYERS;
  const extraIncome = (form.extra_associates || []).reduce((s, a) => s + (parseFloat(a?.income) || 0)
    + ((a?.rentals_list || []).reduce((x, r) => x + (parseFloat(r.rent) || 0), 0)) * PONDERATION_LOYERS, 0);
  const totalRevenusActuels = leadIncome + coIncome + extraIncome;

  // Charges fixes actuelles : credits + charges recurrentes (emprunteur, co-emprunteur, associes)
  const creditMonthly = (form.credits || []).reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  const chargesMonthly = (form.charges_list || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const coCreditMonthly = (form.co_borrower_credits_list || []).reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  const coChargesMonthly = (form.co_borrower_charges_list || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const extraFixed = (form.extra_associates || []).reduce((s, a) =>
    s + (a?.credits_list || []).reduce((x, c) => x + (parseFloat(c.monthly) || 0), 0)
      + (a?.charges_list || []).reduce((x, c) => x + (parseFloat(c.amount) || 0), 0), 0);
  const totalCreditsActuels = creditMonthly + chargesMonthly + coCreditMonthly + coChargesMonthly + extraFixed;

  // Apport : priorite aux apports investisseurs saisis, SINON target_apport (pas d'addition = pas de double comptage)
  const invApport = (parseFloat(form.investor1_project_apport) || 0) + (parseFloat(form.investor2_project_apport) || 0)
    + (form.extra_associates || []).reduce((s, a) => s + (parseFloat(a?.apport) || 0), 0);
  const apport = invApport > 0 ? invApport : (parseFloat(form.target_apport) || 0);

  if (totalRevenusActuels === 0) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="bank-scenarios-empty">
        Renseignez d'abord les revenus mensuels pour activer la simulation automatique.
      </div>
    );
  }

  const rate20 = rates?.rates?.['20'] || 3.85;
  const rate25 = rates?.rates?.['25'] || 4.00;

  return (
    <div data-testid="bank-scenarios">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          Capacite d'emprunt sur les 3 budgets de reference, calculee aux taux du jour
          {rates?.source === 'meilleurtaux.com' && (
            <span className="ml-1 text-xs text-gray-400">(taux Meilleurtaux : 20a {rate20.toFixed(2)}% / 25a {rate25.toFixed(2)}%)</span>
          )}
        </p>
        <div className="flex items-center gap-3 text-xs">
          <span className="px-2 py-1 bg-[#008A8D]/10 text-[#006B6E] font-semibold rounded">Loyer pondere {PONDERATION_LOYERS * 100}%</span>
          <span className="px-2 py-1 bg-amber-100 text-amber-700 font-semibold rounded">Plafond HCSF {PLAFOND_ENDETTEMENT}%</span>
        </div>
      </div>

      {loadingRates && (
        <p className="text-xs text-gray-400 italic">Chargement des taux du jour...</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="bank-scenarios-table">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="p-3 border-b-2 border-gray-200 font-semibold">Scenario</th>
              <th className="p-3 border-b-2 border-gray-200 font-semibold">Loyer attendu</th>
              <th className="p-3 border-b-2 border-gray-200 font-semibold">Sur 20 ans <span className="font-normal text-gray-400">({rate20.toFixed(2)}%)</span></th>
              <th className="p-3 border-b-2 border-gray-200 font-semibold">Sur 25 ans <span className="font-normal text-gray-400">({rate25.toFixed(2)}%)</span></th>
            </tr>
          </thead>
          <tbody>
            {FIXED_SCENARIOS.map(scenario => {
              const empruntable = scenario.budget - apport; // ce qui doit etre emprunte
              const m20 = computeMonthly(empruntable, rate20, 20);
              const m25 = computeMonthly(empruntable, rate25, 25);

              // Revenus futurs : revenus actuels + 80% du loyer prevu
              const revenusFuturs = totalRevenusActuels + scenario.rent * PONDERATION_LOYERS;
              // Endettement futur (credits actuels + nouvelle mensualite) / revenus futurs
              const endet20 = revenusFuturs > 0 ? ((totalCreditsActuels + m20) / revenusFuturs * 100) : 0;
              const endet25 = revenusFuturs > 0 ? ((totalCreditsActuels + m25) / revenusFuturs * 100) : 0;
              const ok20 = endet20 <= PLAFOND_ENDETTEMENT;
              const ok25 = endet25 <= PLAFOND_ENDETTEMENT;

              return (
                <tr key={scenario.budget} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`scenario-row-${scenario.budget}`}>
                  <td className="p-3">
                    <div className="font-bold text-gray-900">{scenario.budget.toLocaleString('fr-FR')} EUR</div>
                    <div className="text-xs text-gray-500">A emprunter : {empruntable.toLocaleString('fr-FR')} EUR</div>
                  </td>
                  <td className="p-3">
                    <span className="font-semibold text-[#A57F4F]">{scenario.rent.toLocaleString('fr-FR')} EUR/mois</span>
                  </td>
                  <td className="p-3">
                    <ScenarioCell monthly={m20} debt={endet20} ok={ok20} testid={`cell-${scenario.budget}-20`} />
                  </td>
                  <td className="p-3">
                    <ScenarioCell monthly={m25} debt={endet25} ok={ok25} testid={`cell-${scenario.budget}-25`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-400 italic">
        Note : ces chiffres de reference (300k/1900EUR, 350k/2500EUR, 400k/3200EUR) sont donnes
        a titre indicatif pour degrossir rapidement la capacite. Pour une estimation plus fine,
        utilisez les "Simulations de projets" ci-dessous ou le Dossier de financement complet.
      </p>
    </div>
  );
};

const ScenarioCell = ({ monthly, debt, ok, testid }) => (
  <div className={`p-2 rounded-lg ${ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid={testid}>
    <div className="text-sm font-bold text-gray-900">
      {Math.round(monthly).toLocaleString('fr-FR')} EUR/mois
    </div>
    <div className="flex items-center justify-between mt-1">
      <span className={`text-xs font-semibold ${ok ? 'text-green-700' : 'text-red-700'}`}>
        {debt.toFixed(1)}% endettement
      </span>
      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${ok ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
        {ok ? 'OK' : 'KO'}
      </span>
    </div>
  </div>
);

export default BankScenariosWidget;
